import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, ArrowRight, CheckCircle2, Facebook, MessageSquare, ChevronRight } from 'lucide-react'
import ContactForm from '../components/ContactForm'

export default function Home() {
  const [fbPosts, setFbPosts] = useState([])
  const [loadingFb, setLoadingFb] = useState(true)

  useEffect(() => {
    // Set SEO Meta
    document.title = 'Craft Café | Catering, Tapas & Mad ud af huset i Horsens'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Professionel og appetitlig catering, tapas, klassisk dansk mad, kager og brød fra Craft Café i Horsens.'
      )
    }

    // Fetch Facebook posts from CMS
    fetch('https://api.dev.auxo.dk/public/content/facebook_posts')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch FB posts')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setFbPosts(data.slice(0, 3)) // Show top 3 posts
        }
        setLoadingFb(false)
      })
      .catch(() => {
        setLoadingFb(false)
      })
  }, [])

  const services = [
    {
      title: 'Mad ud af huset',
      slug: 'mad-ud-af-huset',
      description: 'Få leveret en komplet, velsmagende menu direkte til døren. Perfekt til både uformelle sammenkomster og store mærkedage.',
      image: 'https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/936353aa-d403-40ae-b2b9-fb7280dd631f.jpg',
    },
    {
      title: 'Catering',
      slug: 'catering',
      description: 'Professionel catering til bryllupper, konfirmationer, fødselsdage og firmafester. Vi skræddersyr menuen efter dine ønsker.',
      image: 'https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/dea57bc1-a096-4d5a-b9f9-6185905c6d75.jpg',
    },
    {
      title: 'Tapas',
      slug: 'tapas',
      description: 'Lækre, indbydende tapasfade med udsøgte spanske og nordiske specialiteter, oste, pølser og hjemmelavet dyppelse.',
      image: 'https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/b57f2bcd-6549-44be-b806-62e2567a25ca.jpg',
    },
    {
      title: 'Klassisk dansk mad',
      slug: 'klassisk-dansk-mad',
      description: 'De gode, velkendte danske retter lavet helt fra bunden med kærlighed, tradition og friske, lokale råvarer.',
      image: 'https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/939462d2-00cb-4bf6-ad9c-51f5146eba5a.jpg',
    },
    {
      title: 'Kager & Brød',
      slug: 'kager-og-brod',
      description: 'Friskbagt surdejsbrød, sprøde rundstykker og lækre kager fra vores eget bageri. Bestil nemt online til afhentning.',
      image: 'https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/9c2b893a-626f-4e94-8569-e35755676f4e.jpg',
    },
  ]

  return (
    <div className="pt-[70px] md:pt-[100px]">
      {/* 1. Hero / Brand Introduction Section (Perfect Center Alignment) */}
      <section className="relative w-full min-h-[75vh] flex flex-col items-center justify-center text-center px-6 py-20 md:py-32 gap-y-6 overflow-hidden bg-[#faf9f6]">
        {/* Background Image with 0.5 opacity */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/d0137351-6fb2-49e8-b9ff-9d5f7ea8fbce.jpg"
            alt="Craft Café"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#faf9f6]/95 via-[#faf9f6]/70 to-[#faf9f6]" />
        </div>

        {/* Content Container */}
        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center gap-y-6">
          {/* Sub-logo / Wordmark */}
          <div className="flex flex-col items-center mb-2">
            <span className="font-serif text-2xl tracking-[0.2em] text-[#1c2b21] uppercase font-light leading-none">
              CRAFT CAFÉ
            </span>
            <span className="text-[10px] font-sans font-light uppercase tracking-[0.3em] text-[#b89a6c] mt-2">
              café & catering
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide leading-relaxed text-[#1c2b21] max-w-3xl">
            Mad ud af huset, catering, tapas, kager og brød i Horsens og omegn
          </h1>

          {/* Intro Paragraphs */}
          <div className="max-w-2xl mx-auto space-y-4 text-[#1c2b21] font-light text-sm leading-relaxed drop-shadow-xs">
            <p>
              Velkommen til Craft Café. Vi skaber uforglemmelige madoplevelser til dine gæster med fokus på den gode smag, friske råvarer og det stolte køkkenhåndværk.
            </p>
            <p>
              Uanset om du skal holde en stor fest, en intim fødselsdag, ønsker en hyggelig aften med lækre tapasfade, eller vil bestille friskbagt brød og kager, står vi klar til at levere mad i særklasse, flot anrettet og klar til servering.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="mt-4 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="bg-[#b89a6c] text-white px-8 py-4 text-xs font-semibold uppercase tracking-widest transition-all hover:bg-[#1c2b21] active:scale-95 cursor-pointer rounded-none"
            >
              Bestil / Kontakt os
            </Link>
            <a
              href="#ydelser"
              className="border border-[#b89a6c]/40 text-[#1c2b21] bg-white/40 backdrop-blur-xs px-8 py-4 text-xs font-semibold uppercase tracking-widest transition-all hover:border-[#b89a6c] hover:text-[#b89a6c] active:scale-95 cursor-pointer rounded-none"
            >
              Se muligheder
            </a>
          </div>
        </div>
      </section>

      {/* 3. About Card & Category Navigation Grid */}
      <section id="ydelser" className="py-16 md:py-24 max-w-5xl mx-auto px-6 space-y-16">
        {/* Primary Info Card */}
        <div className="border border-stone-200 bg-[#f4f1ea] p-8 md:p-12 text-center space-y-6 rounded-none">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#b89a6c] font-light">Vores filosofi</span>
          <h2 className="font-serif text-2xl md:text-3xl font-light tracking-wide text-[#1c2b21]">
            Kvalitet uden kompromis
          </h2>
          <div className="max-w-2xl mx-auto space-y-4 text-[#1c2b21]/90 font-light text-sm leading-relaxed">
            <p>
              Hos Craft Café brænder vi for at skabe uforglemmelige madoplevelser til dine gæster. Vi mener, at den gode madoplevelse starter med de rigtige råvarer. Derfor samarbejder vi med lokale leverandører for at sikre friske, smagfulde ingredienser.
            </p>
            <p>
              Vi laver alt fra bunden, lige fra vores pestoer og dressinger til vores varme retter, kager og brød. Vores catering og mad ud af huset er skræddersyet til dine ønsker, så du kan slappe af og nyde festen.
            </p>
          </div>
          <div className="pt-4">
            <Link
              to="/om-os"
              className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#b89a6c] hover:text-[#1c2b21] transition-colors"
            >
              Læs mere om os <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Category Grid (4 Columns) */}
        <div className="space-y-8">
          <div className="text-center space-y-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-light">Hvad vi tilbyder</span>
            <h3 className="font-serif text-2xl font-light text-[#1c2b21] tracking-wide">Vores populære madoplevelser</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link
                key={service.slug}
                to={`/${service.slug}`}
                className="group flex flex-col justify-between border border-stone-200 bg-white hover:border-[#b89a6c] transition-colors duration-300 rounded-none overflow-hidden"
              >
                <div>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-[#faf9f6]/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="p-6 space-y-3">
                    <h4 className="font-serif text-lg font-light text-[#1c2b21] group-hover:text-[#b89a6c] transition-colors">
                      {service.title}
                    </h4>
                    <p className="text-xs text-[#1c2b21]/80 font-light leading-relaxed line-clamp-3">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className="p-6 pt-0 flex items-center justify-between text-[10px] uppercase tracking-widest text-[#b89a6c] font-semibold">
                  <span>Se muligheder</span>
                  <ChevronRight size={12} className="transform group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Facebook Feed Section (Premium Light Theme) */}
      <section className="border-t border-b border-stone-200 bg-[#f4f1ea] py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-12">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#b89a6c]">
                <Facebook size={12} /> Facebook-synkronisering
              </span>
              <h2 className="font-serif text-2xl md:text-3xl font-light text-[#1c2b21] tracking-wide">
                Følg med i vores hverdag
              </h2>
              <p className="text-xs text-[#1c2b21]/80 font-light max-w-xl leading-relaxed">
                Her kan du se vores seneste opdateringer, nyheder og billeder direkte synkroniseret fra vores Facebook-side.
              </p>
            </div>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-stone-200 bg-white px-5 py-2.5 text-[10px] font-semibold uppercase tracking-widest text-[#1c2b21] hover:border-[#b89a6c] hover:text-[#b89a6c] transition-colors rounded-none"
            >
              Besøg os på Facebook <ArrowRight size={12} />
            </a>
          </div>

          {loadingFb ? (
            <div className="grid gap-6 sm:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border border-stone-200 bg-white p-6 h-80 animate-pulse rounded-none" />
              ))}
            </div>
          ) : fbPosts.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-3">
              {fbPosts.map((post) => (
                <div
                  key={post.id}
                  className="flex flex-col justify-between border border-stone-200 bg-white overflow-hidden rounded-none hover:border-stone-300 transition-colors"
                >
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="border border-[#b89a6c]/20 p-1 text-[#b89a6c]">
                          <Facebook size={12} />
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-[#1c2b21] font-medium">Craft Café</span>
                      </div>
                      <span className="text-[10px] text-[#1c2b21]/60 font-light">{post.post_date}</span>
                    </div>
                    <p className="text-xs text-[#1c2b21]/90 font-light leading-relaxed line-clamp-4">
                      {post.post_text}
                    </p>
                  </div>
                  {post.post_image && post.post_image.length > 0 && (
                    <div className="h-44 overflow-hidden border-t border-stone-100">
                      <img
                        src={post.post_image[0]}
                        alt="Facebook opslag"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <div className="px-6 py-4 bg-stone-50 border-t border-stone-100 flex justify-between items-center">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-[#b89a6c] flex items-center gap-1">
                      <MessageSquare size={10} /> Se opslag
                    </span>
                    <a
                      href={post.post_url || 'https://facebook.com'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] uppercase tracking-widest text-[#1c2b21]/60 hover:text-[#b89a6c] transition-colors font-light"
                    >
                      Læs på Facebook
                    </a>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 border border-stone-200 bg-white rounded-none">
              <p className="text-xs text-stone-500 font-light">Ingen Facebook-opslag fundet i øjeblikket.</p>
            </div>
          )}
        </div>
      </section>

      {/* 5. Quick Contact Form ("Bestil hurtigt") */}
      <section className="py-16 md:py-24 max-w-3xl mx-auto px-6">
        <ContactForm />
      </section>
    </div>
  )
}
